"use client";

import { useEffect, useState } from "react";
import { useLingui } from "@lingui/react/macro";

import { EmailCaptureField } from "@marketing-sites/ui/components";
import { useFormFieldPersistence } from "@marketing-sites/hooks/use-persist-form-fields";

import {
  handoffEmailValidations,
  signupUrlForEmail,
} from "../../_utils/signup-handoff";

/**
 * Collects an email address in the hero and carries it into signup, which
 * submits the email step by itself rather than asking for the address again.
 *
 * `pending` blocks a second submit while the browser tears this page down for
 * the navigation, and is cleared if a bfcache Back brings the page back with
 * the flag still set.
 */
export function HeroEmailCapture() {
  const { t } = useLingui();
  const { updateValue } = useFormFieldPersistence("email");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setPending(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const handleSubmit = (address: string) => {
    setPending(true);
    updateValue(address);
    window.location.assign(signupUrlForEmail(address, window.location.href));
  };

  return (
    <EmailCaptureField
      onSubmit={handleSubmit}
      validations={handoffEmailValidations({
        invalid: t`Please enter a valid email address`,
        tooLong: (max) =>
          t`Please use an email address under ${max} characters`,
      })}
      requiredMessage={t`Please enter your work email address`}
      pending={pending}
      label={t`Work email`}
      placeholder={t`Work email`}
      submitLabel={t`Start free trial`}
      pendingLabel={t`Please wait…`}
      analyticsId="btn_home_hero_email_capture_start_free_trial"
      analyticsLabel="Start free trial (email capture)"
      className="mx-auto max-w-[32rem]"
    />
  );
}
